// Client-seitige Orchestrierung für den universellen AI-Import.
// Verantwortlich für:
//   1. Datei → Base64 / Text konvertieren
//   2. Template-Lookup in Firestore (exchangeTemplates)
//   3. POST an /api/analyze-document
//   4. Template speichern nach bestätigtem Import (via /api/save-template)
//   5. importReview-Dokument in Firestore schreiben

import {
  collection, query, where, orderBy, limit, getDocs,
  doc, setDoc, updateDoc, serverTimestamp,
} from '../lib/fsCompat';
import { db } from '../firebase';
import { computeFormatSignature } from '../utils/formatSignature';
import { importCsvFile } from '../utils/simpleCsvImporter';
import { validateTransactions, computeOverallConfidence } from '../utils/importValidation';
import { enrichTimestamps } from '../utils/chainTimestampEnricher';
import { loadTypeSynonyms, loadRecentCorrections } from './learningStore';

// Destilliert aus User-Korrektur-Logs ein kompaktes typ-Mapping
// (alter Wert → korrigierter Wert) für Few-Shot-Prompting.
// Wir senden NUR typ-Diffs ans Modell — Schema-Wissen, das ohnehin schon
// einmal durch dieselbe Pipeline lief (selber User, selbes Format).
function extractTypeMappings(corrections) {
  const map = new Map();
  for (const c of corrections || []) {
    for (const e of c.entries || []) {
      for (const d of e.diff || []) {
        if (d.field === 'typ' && d.oldVal && d.newVal && d.oldVal !== d.newVal) {
          map.set(String(d.oldVal).toLowerCase().trim(), String(d.newVal));
        }
      }
    }
  }
  return Array.from(map.entries()).map(([from, to]) => ({ from, to }));
}

const API_BASE = process.env.REACT_APP_API_BASE || '';

// ── TEMPLATE LOOKUP ───────────────────────────────────────────────────────────

export async function findTemplate(formatSignature) {
  if (!formatSignature) return null;
  try {
    const q = query(
      collection(db, 'exchangeTemplates'),
      where('formatSignature', '==', formatSignature),
      orderBy('confidence', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  } catch (err) {
    console.warn('[documentIngestion] Template-Lookup fehlgeschlagen:', err.message);
    return null;
  }
}

// ── DATEI → INHALT ────────────────────────────────────────────────────────────

export async function fileToIngestionPayload(file) {
  const name = file.name.toLowerCase();
  const ext = name.split('.').pop();

  if (ext === 'pdf') {
    const base64 = await fileToBase64(file);
    return { content: base64, contentType: 'pdf', mimeType: 'application/pdf' };
  }

  if (ext === 'xlsx' || ext === 'xls') {
    const base64 = await fileToBase64(file);
    return { content: base64, contentType: 'xlsx', mimeType: file.type };
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    const base64 = await fileToBase64(file);
    return { content: base64, contentType: 'image', mimeType: file.type || 'image/jpeg' };
  }

  // CSV / TXT — als Text lesen
  const text = await fileToText(file);
  return { content: text, contentType: 'csv', mimeType: 'text/csv' };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Nur den Base64-Teil (ohne data:…;base64, Prefix)
      const result = reader.result;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function fileToText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file, 'UTF-8');
  });
}

// ── CSV-HEADER EXTRAHIEREN (client-seitig für Template-Suche) ─────────────────

export function extractCsvHeaders(csvText) {
  if (!csvText) return [];
  const clean = csvText.charCodeAt(0) === 0xFEFF ? csvText.slice(1) : csvText;
  const lines = clean.split('\n').filter(l => l.trim());
  if (!lines.length) return [];
  // Delimiter erkennen
  const first = lines[0];
  const sep = (first.match(/\t/g) || []).length > 3 ? '\t'
             : (first.match(/;/g) || []).length > (first.match(/,/g) || []).length ? ';'
             : ',';
  return first.split(sep).map(h => h.trim().replace(/"/g, ''));
}

// ── HAUPT-ANALYSE ─────────────────────────────────────────────────────────────

// Gibt das API-Analyse-Ergebnis zurück (mit Transactions, Confidence, etc.)
// Sucht vorab in Firestore nach einem passenden Template.
//
// options.uid: aktuelle User-Id — wenn gesetzt, werden gespeicherte
// User-Synonyme (Per-User-Learning) auf das KI-Ergebnis angewendet.
export async function analyzeDocument(file, options = {}) {
  const { uid } = options;
  const payload = await fileToIngestionPayload(file);

  // Per-User-Synonyme vorab laden (kleine Collection, schnell)
  const synonyms = uid ? await loadTypeSynonyms(uid) : {};
  const applySynonyms = (txs) => {
    if (!Object.keys(synonyms).length) return txs;
    return (txs || []).map(t => {
      const raw = String(t?.typ || '').toLowerCase().trim();
      const mapped = synonyms[raw];
      return mapped && mapped !== t.typ ? { ...t, typ: mapped } : t;
    });
  };

  // ── Deterministischer Pre-Check für bekannte CSV-Formate ──────────────────────
  // Bekannte Exchanges werden OHNE KI-Aufruf direkt geparst — schneller,
  // deterministisch und kostet kein API-Budget.
  // Nur bei Generic-Erkennung oder leerem Ergebnis fällt der Code auf die KI zurück.
  if (payload.contentType === 'csv') {
    try {
      const { exchange, txs, failed, warnings, stats } = await importCsvFile(file);
      if (exchange !== 'Generic' && txs.length > 0) {
        if (failed.length > 0) {
          console.warn(`[documentIngestion] ${failed.length} Zeilen fehlgeschlagen:`, failed);
        }
        if (warnings.length > 0) {
          console.warn('[documentIngestion] Warnungen:', warnings);
        }
        const withSynonyms = applySynonyms(txs);
        // Block-Zeitstempel fuer TXs mit Hash aber ohne Sekunden-Praezision
        // nachziehen (Blockchair/GoldRush). Silent — wenn der Endpoint nicht
        // antwortet oder die Chain unbekannt ist, bleibt die TX wie sie war.
        let enriched = withSynonyms;
        try {
          const r = await enrichTimestamps(withSynonyms);
          enriched = r.txs;
          if (r.enrichedCount > 0) {
            console.log(`[documentIngestion] ${r.enrichedCount} Zeitstempel aus Blockchain ergaenzt`);
          }
        } catch (err) {
          console.warn('[documentIngestion] Chain-Zeitstempel-Enrichment fehlgeschlagen:', err);
        }
        const validated = validateTransactions(enriched);
        const overallConfidence = computeOverallConfidence(validated);
        return {
          exchange,
          templateId:        null,
          transactions:      validated,
          overallConfidence,
          requiresReview:    overallConfidence < 0.82,
          isPartial:         stats.failed > 0,
          totalRows:         stats.totalRows,
          processedRows:     stats.parsed,
          notes:             warnings.length > 0 ? warnings.join(' | ') : null,
          formatSignature:   null,
          _source:           'deterministic',
        };
      }
    } catch (err) {
      // Parser schlägt fehl → KI-Fallback
      console.warn('[documentIngestion] Deterministischer Parser fehlgeschlagen:', err.message);
    }
  }

  // Template-Lookup nur bei CSV/TXT/XLSX (PDF/Bild haben keine Spaltenstruktur)
  let template = null;
  let formatSignature = null;
  if (['csv', 'txt', 'xlsx'].includes(payload.contentType) && payload.contentType !== 'xlsx') {
    const headers = extractCsvHeaders(payload.content);
    if (headers.length > 0) {
      formatSignature = computeFormatSignature(headers);
      template = await findTemplate(formatSignature);
    }
  }

  // Few-Shot: Frühere Korrekturen DIESES Users für DIESES Format laden.
  // Wird nur weitergereicht wenn formatSignature bekannt ist (CSV/TXT).
  let priorTypeMappings = [];
  if (uid && formatSignature) {
    const corrections = await loadRecentCorrections(uid, formatSignature, 10);
    priorTypeMappings = extractTypeMappings(corrections).slice(0, 8);
  }

  const body = {
    content:        payload.content,
    contentType:    payload.contentType,
    mimeType:       payload.mimeType,
    fileName:       file.name,
    ...(template ? { columnMappings: template.columnMappings, templateId: template.id } : {}),
    ...(priorTypeMappings.length ? { priorTypeMappings } : {}),
  };

  const auth = await (await import('./authHeader')).authHeader();
  const res = await fetch(`${API_BASE}/api/analyze-document`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unbekannter Fehler' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  const data = await res.json();
  return {
    ...data,
    transactions: applySynonyms(data.transactions),
    templateUsed: template,
    formatSignature: data.formatSignature || formatSignature,
  };
}

// ── TEMPLATE SPEICHERN ────────────────────────────────────────────────────────

// Wird nach bestätigtem Import aufgerufen wenn kein Template existierte
// oder um useCount/confidence eines bestehenden Templates zu erhöhen.
//
// DSGVO: Templates enthalten KEINE personenbezogenen Daten mehr —
// nur Schema-Wissen (Spaltenmappings + Format-Signatur). 'createdBy' ist die
// User-uid, damit Templates bei Account-Löschung zuordenbar sind.
export async function saveOrUpdateTemplate({ uid, formatSignature, exchange, columnMappings, existingTemplateId, overallConfidence }) {
  if (!formatSignature || !columnMappings) return;

  try {
    if (existingTemplateId) {
      // Bestehendes Template: useCount++ + rolling confidence direkt via Client-SDK
      const ref = doc(db, 'exchangeTemplates', existingTemplateId);
      const snap = await getDocs(query(collection(db, 'exchangeTemplates'), where('__name__', '==', existingTemplateId), limit(1)));
      if (!snap.empty) {
        const { useCount = 0, confidence = 0.8 } = snap.docs[0].data();
        const newUseCount = useCount + 1;
        const newConfidence = (confidence * useCount + (overallConfidence ?? confidence)) / newUseCount;
        await updateDoc(ref, {
          useCount: newUseCount,
          confidence: Math.min(0.999, newConfidence),
          updatedAt: serverTimestamp(),
        });
      }
    } else if (overallConfidence >= 0.90) {
      // Neues Template nur bei hoher Confidence
      const slug = `${(exchange || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`;
      const ref = doc(db, 'exchangeTemplates', slug);
      await setDoc(ref, {
        templateId:      slug,
        exchange:        exchange || 'Unbekannt',
        formatSignature,
        columnMappings,
        // sampleRows bewusst NICHT mehr gespeichert (DSGVO: enthielten echte
        // Transaktionszeilen anderer User in einer cross-user Collection).
        confidence:      overallConfidence,
        useCount:        1,
        createdBy:       uid || 'anonymous',
        createdAt:       serverTimestamp(),
        updatedAt:       serverTimestamp(),
      });
    }
  } catch (err) {
    // Template-Speichern ist nicht kritisch — Fehler still schlucken
    console.warn('[documentIngestion] Template speichern fehlgeschlagen:', err.message);
  }
}

// ── REVIEW DOKUMENT ───────────────────────────────────────────────────────────

// Schreibt ein importReviews-Dokument für Low-Confidence Imports
export async function createImportReview(uid, { transactions, overallConfidence, exchange, fileName, templateId }) {
  const reviewId = `review_${Date.now()}`;
  const ref = doc(db, 'users', uid, 'importReviews', reviewId);
  await setDoc(ref, {
    importId:          reviewId,
    templateId:        templateId || null,
    status:            'pending',
    transactions,
    corrections:       [],
    overallConfidence,
    exchange:          exchange || 'Unbekannt',
    fileName:          fileName || '',
    createdAt:         serverTimestamp(),
    reviewedAt:        null,
  });
  return reviewId;
}

// Markiert ein Review als abgeschlossen.
// corrections (optional) wird mitgespeichert für Few-Shot-Learning.
export async function completeImportReview(uid, reviewId, status = 'approved', corrections = null) {
  const ref = doc(db, 'users', uid, 'importReviews', reviewId);
  const update = { status, reviewedAt: serverTimestamp() };
  if (corrections && corrections.length > 0) {
    update.corrections = corrections;
  }
  await updateDoc(ref, update);
}
